import React from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import FretQuoteForm from '../components/FretQuoteForm';
import FretPriceSimulator from '../components/FretPriceSimulator';
import { Card, CardContent } from '../components/ui/card';
import {
  Car, Package, Home, Building2, Ship, Truck, FileCheck,
  MapPin, CheckCircle, Ruler, ShieldCheck, Headphones
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const SERVICES = [
  {
    icon: Car,
    title: 'Véhicules',
    desc: 'Voitures, motos, quads, scooters, jet-skis. Transport en RoRo ou conteneur.',
    features: ['Enlèvement partout en France', 'Dédouanement inclus', 'Assurance transport'],
    color: 'from-blue-600 to-cyan-500',
    fromPrice: '850 €'
  },
  {
    icon: Package,
    title: 'Palettes / Groupage',
    desc: 'De la palette isolée au groupage complet, vers DOM-TOM et international.',
    features: ['1 à 50 palettes', 'Réception entrepôt ou enlèvement', 'Suivi en temps réel'],
    color: 'from-emerald-600 to-teal-500',
    fromPrice: '180 €'
  },
  {
    icon: Home,
    title: 'Déménagement',
    desc: 'Conteneur 20\' / 40\' dédié, groupage possible pour petits volumes.',
    features: ['Emballage sur demande', 'Liste d\'inventaire fournie', 'Livraison à domicile'],
    color: 'from-purple-600 to-indigo-500',
    fromPrice: '2 500 €'
  },
  {
    icon: Building2,
    title: 'Fret grossiste',
    desc: 'Solution logistique complète pour e-commerçants et grossistes.',
    features: ['Tarifs dégressifs', 'Envois récurrents', 'Interlocuteur dédié'],
    color: 'from-orange-600 to-amber-500',
    fromPrice: 'Sur devis'
  }
];

const PROCESS = [
  { icon: FileCheck, title: '1. Demande de devis', desc: 'Remplissez le formulaire en ligne (2 min). Recevez votre devis personnalisé sous 24h.' },
  { icon: Truck, title: '2. Enlèvement', desc: 'Nous récupérons votre marchandise partout en France à la date convenue.' },
  { icon: Ship, title: '3. Transport maritime', desc: 'Acheminement vers le port, mise en conteneur, traversée sécurisée.' },
  { icon: MapPin, title: '4. Dédouanement', desc: 'Formalités douanières prises en charge à l\'arrivée (véhicules inclus).' },
  { icon: Home, title: '5. Livraison finale', desc: 'Livraison au port, à votre domicile ou votre entrepôt selon votre choix.' },
];

export default function FretPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="fret-page">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #60a5fa 0%, transparent 50%), radial-gradient(circle at 80% 80%, #3b82f6 0%, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/20 border border-blue-400/30 text-blue-200 rounded-full text-sm font-semibold mb-6">
                <Ship className="w-4 h-4" />
                Nouveau — Service Fret & Logistique
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6 leading-tight">
                Envoyez tout,<br />
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  partout dans le monde
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed">
                Véhicules, motos, jet-skis, palettes, déménagements...
                <strong className="text-white"> Enlèvement France, transport maritime, dédouanement, livraison.</strong>
              </p>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-400" /> Devis sous 24h</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-400" /> Assurance cargo</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-400" /> Service français</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <FretPriceSimulator />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-14">
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 tracking-tight mb-4">
              Nos services fret
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une solution adaptée à chaque besoin, du particulier au grossiste.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <Card className="h-full border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                    <CardContent className="p-7">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">À partir de</div>
                          <div className="font-heading font-bold text-2xl text-slate-900">{s.fromPrice}</div>
                        </div>
                      </div>
                      <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{s.title}</h3>
                      <p className="text-slate-600 mb-4">{s.desc}</p>
                      <ul className="space-y-2">
                        {s.features.map((f, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-slate-700">
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-14">
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 tracking-tight mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              De la demande au colis livré : un processus clair, sans surprise.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="relative"
                >
                  <div className="bg-white rounded-xl p-5 border border-slate-200 h-full hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="font-heading font-bold text-slate-900 mb-2">{p.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="devis" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <motion.div {...fadeInUp} className="lg:col-span-2">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 tracking-tight mb-4">
                Demandez votre devis gratuit
              </h2>
              <p className="text-slate-600 mb-6">
                Notre équipe analyse votre besoin et vous envoie un devis personnalisé
                sous <strong>24h ouvrées</strong>. Sans engagement.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Assurance cargo incluse</h4>
                    <p className="text-sm text-slate-600">Vos biens sont assurés pendant tout le transport.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Ruler className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Aucune limite de taille</h4>
                    <p className="text-sm text-slate-600">De la palette au conteneur 40' complet.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Service client en français</h4>
                    <p className="text-sm text-slate-600">Un interlocuteur dédié à votre dossier.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="lg:col-span-3">
              <FretQuoteForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
